/**
 * Build raw script export bundle files (manifest + orchestrator shell script).
 */
import type { RawExportBundle } from './query'
import type { TranscriptChunkProject } from './project'
import { parseTimecodeToSeconds } from './timecode'
import { slugifyProjectName } from './project'

export type RawScriptBundleFiles = {
  manifestJson: string
  runScript: string
  baseName: string
}

function safeClipFilename (
  project: TranscriptChunkProject,
  index: number,
  clip: TranscriptChunkProject['clips'][number],
) {
  const title = clip.contentTitle || clip.seriesTitle || `clip-${index + 1}`
  const slug = slugifyProjectName(title).slice(0, 48) || `clip-${index + 1}`
  return `${String(index + 1).padStart(2, '0')}-${slug}.mp4`
}

export function buildRawScriptBundle (
  project: TranscriptChunkProject,
  bundle: RawExportBundle,
): RawScriptBundleFiles {
  const sourceByMediaId = new Map(
    bundle.sources.map((source) => [String(source.featureMediaId), source]),
  )
  const manifestSources = bundle.sources.map((source) => {
    const fileName = source.fileName || `source-${source.featureMediaId}.mp4`
    return {
      sourceKey: String(source.featureMediaId),
      featureMediaId: String(source.featureMediaId),
      fileName,
      localPath: `sources/${fileName}`,
      s3Uri: source.s3Uri,
      availability: source.availability,
      downloadUrl: source.downloadUrl,
      downloadUrlExpiresAt: source.downloadUrlExpiresAt,
    }
  })

  const licensedClips = project.clips.filter((clip) => clip.isLicensed)
  const manifest = {
    schemaVersion: 1,
    exportType: 'raw-script',
    generatedAt: new Date().toISOString(),
    warnings:
      licensedClips.length > 0
        ? [
            `${licensedClips.length} licensed clip(s) included — verify usage rights before publishing.`,
          ]
        : [],
    project: {
      id: project.id,
      name: project.name,
    },
    sources: manifestSources,
    clips: project.clips.map((clip, index) => {
      const source = sourceByMediaId.get(String(clip.featureMediaId))
      const fileName = source?.fileName || `source-${clip.featureMediaId}.mp4`
      return {
        index: index + 1,
        clipId: clip.id,
        sourceKey: String(clip.featureMediaId),
        startSeconds: parseTimecodeToSeconds(clip.startTimecode),
        endSeconds: parseTimecodeToSeconds(clip.endTimecode),
        outputPath: `clips/${safeClipFilename(project, index, clip)}`,
        contentTitle: clip.contentTitle,
        isLicensed: Boolean(clip.isLicensed),
        localSourcePath: `sources/${fileName}`,
      }
    }),
    downloadUrlExpiresAt: bundle.presignExpiresAt,
  }

  const embeddedManifest = JSON.stringify(manifest, null, 2)

  const runScript = `#!/usr/bin/env bash
set -euo pipefail

# Requires: bash, curl, jq, ffmpeg (trim/all only). macOS: brew install jq ffmpeg
# Embeds raw-script-manifest.json — written beside this script on first run. No API calls.
#
# First run:
#   chmod +x raw-script.sh
#
# Usage:
#   ./raw-script.sh [download|trim|all|help] [--force]
#   ./raw-script.sh            # same as all (download sources, then trim clips)
#   ./raw-script.sh download   # curl presigned sources into ./sources/
#   ./raw-script.sh trim       # ffmpeg trim into ./clips/ (run after download)
#   ./raw-script.sh trim --force   # re-trim every clip (overwrite ./clips/)
#   ./raw-script.sh help       # show commands

MANIFEST="raw-script-manifest.json"
FORCE_TRIM=0
# Re-encode when stream-copy audio/video start times differ by more than this (seconds).
AV_START_GAP_REENCODE_SECONDS=1.0

bootstrap_manifest_if_needed () {
  if [[ -f "$MANIFEST" ]]; then
    return
  fi
  cat > "$MANIFEST" <<'__EMBEDDED_MANIFEST__'
${embeddedManifest}
__EMBEDDED_MANIFEST__
}

usage () {
  cat <<'EOF'
Usage: ./raw-script.sh [command]

Commands:
  all        Download sources, then trim clips (default)
  download   Download sources only (curl from manifest)
  trim       Trim clips only (requires ./sources/ from a prior download)
  trim --force   Re-trim all clips (overwrite existing files in ./clips/)

Options (trim / all):
  --force, -f   Overwrite existing clips instead of skipping them
  help       Show this help

First time: chmod +x raw-script.sh
Run download within 2 hours of export while presigned URLs are valid.
EOF
}

require_manifest () {
  bootstrap_manifest_if_needed
  if [[ ! -f "$MANIFEST" ]]; then
    echo "Missing $MANIFEST in the current directory." >&2
    exit 1
  fi
}

print_warnings () {
  require_manifest
  while IFS= read -r warning; do
    if [[ -n "$warning" ]]; then
      echo "Warning: $warning" >&2
    fi
  done < <(jq -r '.warnings[]? // empty' "$MANIFEST")
}

cmd_download () {
  require_manifest
  print_warnings
  mkdir -p sources
  while IFS= read -r row; do
    url=$(jq -r '.downloadUrl // empty' <<<"$row")
    dest=$(jq -r '.localPath' <<<"$row")
    expires=$(jq -r '.downloadUrlExpiresAt // empty' <<<"$row")
    if [[ -z "$url" ]]; then
      echo "Missing downloadUrl for $(jq -r '.sourceKey' <<<"$row"). Reopen the admin project link for a fresh bundle." >&2
      exit 1
    fi
    if [[ -n "$expires" ]]; then
      now_epoch=$(date -u +%s)
      exp_epoch=$(jq -r 'if .downloadUrlExpiresAt then (.downloadUrlExpiresAt | if contains(".") then split(".")[0] + "Z" else . end | fromdateiso8601) else 0 end' <<<"$row")
      if [[ "$exp_epoch" != "0" && "$now_epoch" -gt "$exp_epoch" ]]; then
        echo "Presigned URL expired for $(jq -r '.sourceKey' <<<"$row"). Reopen the admin project link for a fresh complete bundle." >&2
        exit 1
      fi
    fi
    echo "Downloading $dest"
    curl -fL "$url" -o "$dest"
  done < <(jq -c '.sources[]' "$MANIFEST")
  echo "All sources downloaded to ./sources/"
}

clip_has_video_stream () {
  local file="$1"
  ffprobe -v error -select_streams v:0 -show_entries stream=index -of csv=p=0 "$file" 2>/dev/null | grep -q .
}

clip_av_start_gap_seconds () {
  local file="$1"
  local video_start audio_start
  video_start=$(ffprobe -v error -select_streams v:0 -show_entries stream=start_time -of csv=p=0 "$file" 2>/dev/null | head -1)
  audio_start=$(ffprobe -v error -select_streams a:0 -show_entries stream=start_time -of csv=p=0 "$file" 2>/dev/null | head -1)
  if [[ -z "$video_start" || -z "$audio_start" ]]; then
    echo "0"
    return
  fi
  awk -v v="$video_start" -v a="$audio_start" 'BEGIN { d = v - a; if (d < 0) d = -d; printf "%.3f", d }'
}

trim_clip_copy () {
  local input="$1" start="$2" duration="$3" output="$4"
  # -ss after -i keeps end time accurate on legacy MP4 containers (stream copy).
  ffmpeg -nostdin -hide_banner -loglevel error -y -i "$input" -ss "$start" -t "$duration" -c copy -avoid_negative_ts make_zero "$output"
}

trim_clip_reencode () {
  local input="$1" start="$2" duration="$3" output="$4"
  # -ss before -i for faster seek on long sources; re-encode when copy is unreliable.
  ffmpeg -nostdin -hide_banner -loglevel error -y -ss "$start" -i "$input" -t "$duration" -c:v libx264 -crf 18 -preset veryfast -c:a aac -movflags +faststart "$output"
}

trim_clip_finalize () {
  local index="$1" input="$2" start="$3" duration="$4" output="$5"
  local gap reason
  if ! clip_has_video_stream "$output"; then
    reason="stream copy lost video (legacy container)"
  else
    gap=$(clip_av_start_gap_seconds "$output")
    if awk -v g="$gap" -v t="$AV_START_GAP_REENCODE_SECONDS" 'BEGIN { exit !(g > t) }'; then
      reason="stream copy A/V start gap \${gap}s"
    fi
  fi
  if [[ -n "\${reason:-}" ]]; then
    echo "Clip $index: \${reason}; re-encoding..." >&2
    rm -f "$output"
    trim_clip_reencode "$input" "$start" "$duration" "$output"
    if ! clip_has_video_stream "$output"; then
      echo "Clip $index: trim failed — no video stream in output" >&2
      exit 1
    fi
    gap=$(clip_av_start_gap_seconds "$output")
    if awk -v g="$gap" -v t="$AV_START_GAP_REENCODE_SECONDS" 'BEGIN { exit !(g > t) }'; then
      echo "Clip $index: trim failed — A/V start gap \${gap}s after re-encode" >&2
      exit 1
    fi
  fi
}

cmd_trim () {
  require_manifest
  mkdir -p clips
  while IFS= read -r row; do
    [[ -z "$row" ]] && continue
    index=$(jq -r '.index' <<<"$row")
    start=$(jq -r '.startSeconds' <<<"$row")
    end=$(jq -r '.endSeconds' <<<"$row")
    input=$(jq -r '.localSourcePath' <<<"$row")
    output=$(jq -r '.outputPath' <<<"$row")
    if [[ ! -f "$input" ]]; then
      echo "Missing source file for clip $index: $input (run ./raw-script.sh download first)" >&2
      exit 1
    fi
    if [[ -f "$output" && "$FORCE_TRIM" != "1" ]]; then
      echo "Skipping clip $index (already exists): $output"
      continue
    fi
    if [[ -f "$output" ]]; then
      echo "Re-trimming clip $index (overwriting): $output"
      rm -f "$output"
    fi
    duration=$(awk -v s="$start" -v e="$end" 'BEGIN { printf "%.3f", e - s }')
    echo "Trimming clip $index -> $output (\${duration}s)"
    trim_clip_copy "$input" "$start" "$duration" "$output"
    trim_clip_finalize "$index" "$input" "$start" "$duration" "$output"
  done < <(jq -c '.clips[]' "$MANIFEST")
  echo "Trimmed clips written to ./clips/"
}

cmd_all () {
  cmd_download
  cmd_trim
}

main () {
  local command="\${1:-all}"
  shift $(( $# > 0 ? 1 : 0 )) || true
  while [[ $# -gt 0 ]]; do
    case "$1" in
      --force|-f)
        FORCE_TRIM=1
        ;;
      *)
        echo "Unknown option: $1" >&2
        usage >&2
        exit 1
        ;;
    esac
    shift
  done
  case "$command" in
    all)
      cmd_all
      ;;
    download|download-only)
      cmd_download
      ;;
    trim|trim-only)
      cmd_trim
      ;;
    help|-h|--help)
      usage
      ;;
    *)
      echo "Unknown command: $command" >&2
      usage >&2
      exit 1
      ;;
  esac
}

main "$@"
`

  return {
    manifestJson: `${JSON.stringify(manifest, null, 2)}\n`,
    runScript,
    baseName: slugifyProjectName(project.name) || 'raw-script',
  }
}

export function downloadRawScriptBundleFiles (files: RawScriptBundleFiles) {
  const blob = new Blob([files.runScript], { type: 'text/x-shellscript' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'raw-script.sh'
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
